import BeeswaxCatchupBehavior from '@/Enums/Beeswax/BeeswaxCatchupBehavior.enum';
import BeeswaxPacing from '@/Enums/Beeswax/BeeswaxPacing.enum';
import BeeswaxPacingBehavior from '@/Enums/Beeswax/BeeswaxPacingBehavior.enum';
import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import DateFormat from '@/Enums/Format/DateFormat.enum';
import ItemStatus from '@/Enums/ItemStatus.enum';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import LineItemService from '@/Services/CampaignManagement/LineItem.service';
import { PRODUCTS_LABELS } from '@/constants/beeswax';
import {
  CAMPAIGN_MANAGEMENT_ROW_TYPE,
  DEFAULT_ASSOCIATION_WEIGHT_REL,
  STATUS_OPTIONS,
} from '@/constants/campaignManagement';
import { LEVEL_VALUES, PACING_STATUS_VALUES } from '@/constants/pacing';
import { normalizeUrl } from '@/helpers/url';
import moment from 'moment';
import { nanoid } from 'nanoid';
import { getLevelFilter, getPacingStatus, shouldCutHierarchyToTopLevel } from './common';
import { isoDateWithoutTime } from './dateFormatter';
import { getControlOptionsValues } from './filters/filterMapping';
import { getSortFilter } from './grid/getSortFilter';
import { removeEmptyKeys } from './object';
import { isAdminRole } from './roles';
import { numberToUsFormat } from './valueFormatter/numberFormatter.helpers';

const getTopProductValue = lineItems => {
  const products = lineItems.flatMap(({ product }) => product).filter(Boolean);

  if (!products.length) {
    return '—';
  }

  const productsNames = products.map(({ name }) => name);

  if (productsNames.includes(PRODUCTS_LABELS.GOLD)) {
    return PRODUCTS_LABELS.GOLD;
  }

  if (productsNames.includes(PRODUCTS_LABELS.SILVER)) {
    return PRODUCTS_LABELS.SILVER;
  }

  if (productsNames.includes(PRODUCTS_LABELS.BRONZE)) {
    return PRODUCTS_LABELS.BRONZE;
  }

  return productsNames[0];
};

const aggregateFrequencyCaps = lineItems => {
  if (!lineItems.length) {
    return null;
  }

  const capSums = {};
  const capCounts = {};

  lineItems.forEach(({ frequency_caps }) => {
    Object.entries(frequency_caps || {}).forEach(([cap, value]) => {
      capSums[cap] = (capSums[cap] || 0) + value;
      capCounts[cap] = (capCounts[cap] || 0) + 1;
    });
  });

  const averages = {};

  if (Object.keys(capSums).length) {
    Object.keys(capSums).forEach(cap => {
      const count = capCounts[cap];
      if (count > 0) {
        averages[cap] = numberToUsFormat(capSums[cap] / count);
      }
    });
  }

  return averages;
};

const getOsi = pacingStatuses => {
  const osi = [];

  pacingStatuses.forEach(status => {
    if (status === PACING_STATUS_VALUES.UNDER_PACING) {
      osi.push({ from: 0.1, to: 0.84 });
    } else if (status === PACING_STATUS_VALUES.RISK_PACING) {
      osi.push({ from: 0.85, to: 0.94 });
    } else if (status === PACING_STATUS_VALUES.ON_TARGET) {
      osi.push({ from: 0.95, to: 1.01 });
    } else if (status === PACING_STATUS_VALUES.OVER_PACING) {
      osi.push({ from: 1.02, to: 10 });
    }
  });

  return osi;
};

const getGeo = ({ geo }) => {
  return geo?.included?.length ? geo.included : (geo?.excluded ?? []);
};

const getCampaignGeo = allGeo => {
  const uniqueGeo = new Map();

  allGeo.forEach(geo => {
    if (!uniqueGeo.has(geo.id)) {
      uniqueGeo.set(geo.id, geo);
    }
  });

  return Array.from(uniqueGeo.values());
};

const getCampaignHasAudiencesHint = lineItems => {
  return (
    lineItems?.some(({ audienceMeta }) =>
      Object.values(audienceMeta).some(audiences => AudienceTargetingService.checkAudiencesInTargeting(audiences)),
    ) && [
      [
        {
          name: 'Expand Campaign to see the Audiences',
          slug: '',
        },
      ],
    ]
  );
};

const mapValues = arr => arr.map(item => item.value).filter(Boolean);

export const getCampaignManagementFilters = (filters, role, allClients) => {
  const {
    clients,
    advertisers,
    campaigns,
    dateRangeStart,
    dateRangeEnd,
    status,
    pacingStatus,
    channelTypes,
    sort,
    isArchived,
  } = filters;
  const appliedFilter = {
    page: filters.page,
    limit: filters.limit,
    search: filters.search,
  };

  if (campaigns?.length) {
    appliedFilter.campaigns = mapValues(campaigns);
  } else if (advertisers?.length) {
    appliedFilter.advertisers = mapValues(advertisers);
  } else if (clients?.length) {
    appliedFilter.advertisers = clients
      .map(item => mapValues(item.advertisers))
      .flat()
      .filter(Boolean);
  } else if (!clients?.length && !isAdminRole(role)) {
    appliedFilter.advertisers = allClients
      .map(item => mapValues(item.advertisers))
      .flat()
      .filter(Boolean);
  }

  if (status?.length && status.length !== STATUS_OPTIONS.length) {
    appliedFilter.status = status;
  }

  if (dateRangeStart) {
    appliedFilter.start_date = dateRangeStart;
  }

  if (dateRangeEnd) {
    appliedFilter.end_date = dateRangeEnd;
  }

  if (pacingStatus?.length) {
    appliedFilter.osi = getOsi(pacingStatus.map(({ value }) => value));
  }

  if (channelTypes?.length) {
    appliedFilter.channels = mapValues(channelTypes);
  }

  if (sort?.length) {
    appliedFilter.sort = getSortFilter(sort);
  }

  if (isArchived && isAdminRole(role)) {
    appliedFilter.is_archived = isArchived;
  }

  return appliedFilter;
};

export const getProcessedCampaignsReport = (campaigns, filters) => {
  if (!campaigns.length) {
    return [];
  }

  const { level: filterLevel } = filters;
  const shouldIncludeLevelInReport = getLevelFilter(filterLevel);
  const includeCampaignLevel = shouldIncludeLevelInReport(LEVEL_VALUES.CAMPAIGN);
  const includeLineItemLevel = shouldIncludeLevelInReport(LEVEL_VALUES.LINE_ITEM);
  const includeFlightLevel = shouldIncludeLevelInReport(LEVEL_VALUES.FLIGHT);
  const shouldCutHierarchy = shouldCutHierarchyToTopLevel(filterLevel);

  return campaigns.flatMap((campaign, campaignIndex) =>
    processCampaign(campaign, campaignIndex, {
      includeCampaignLevel,
      includeLineItemLevel,
      includeFlightLevel,
      shouldCutHierarchy,
    }),
  );
};

export const processCampaign = (campaign, campaignIndex, options) => {
  const { includeCampaignLevel, includeLineItemLevel, includeFlightLevel, shouldCutHierarchy } = options;
  const campaignHierarchyKey = nanoid();
  const campaignRow = includeCampaignLevel && createCampaignRow(campaign, campaignIndex, campaignHierarchyKey);

  const lineItemsRows =
    includeLineItemLevel || includeFlightLevel
      ? campaign.lineItems.flatMap((lineItem, lineItemIndex) =>
          processLineItem(lineItem, lineItemIndex, campaign, campaignIndex, campaignHierarchyKey, {
            includeLineItemLevel,
            includeFlightLevel,
            shouldCutHierarchy,
          }),
        )
      : [];

  return [campaignRow, ...lineItemsRows].filter(Boolean);
};

const createCampaignRow = (campaign, campaignIndex, campaignHierarchyKey) => ({
  id: `${campaignIndex}_c`,
  campaignId: campaign.id,
  type: CAMPAIGN_MANAGEMENT_ROW_TYPE.CAMPAIGN,
  hierarchy: [campaignHierarchyKey],
  campaignName: campaign.name,
  advertiserName: campaign.advertiser.name,
  whitelabel: campaign.whitelabelName,
  status: campaign.status,
  delivered: campaign.impressions,
  booked: campaign.impressions_budget,
  creativesCount: campaign.lineItems?.reduce((acc, curr) => acc + (curr.creativesCount || 0), 0) || 0,
  pacingStatus: getPacingStatus(
    campaign.lineItems?.reduce((min, { osi }) => (osi > 0 && (osi < min || min === 0) ? osi : min), 0),
  ),
  pacingTo: campaign.lineItems?.reduce((acc, curr) => (curr.osi > acc.osi ? curr : acc), campaign.lineItems[0])?.osi,
  frequencyCaps: aggregateFrequencyCaps(campaign.lineItems),
  geo: getCampaignGeo(campaign.lineItems?.map(getGeo).flat()),
  audience: getCampaignHasAudiencesHint(campaign.lineItems),
  channel: [...new Set(campaign.lineItems?.map(({ channel }) => channel?.name).filter(Boolean) || [])].join(', '),
  product: getTopProductValue(campaign.lineItems),
  startDate: campaign.start_date,
  endDate: campaign.end_date,
  updateDate: campaign.update_date,
  lineItemsCount: campaign.lineItemsCount,
  isArchived: campaign.is_archived,
  altId: campaign.alt_id,
  platform: campaign.platform || CampaignPlatformType.Omnichannel,
});

const processLineItem = (lineItem, lineItemIndex, campaign, campaignIndex, campaignHierarchyKey, options) => {
  const { includeLineItemLevel, includeFlightLevel, shouldCutHierarchy } = options;
  const lineItemHierarchyKey = nanoid();

  const lineItemRow =
    includeLineItemLevel &&
    createLineItemRow(
      lineItem,
      lineItemIndex,
      campaign,
      campaignIndex,
      campaignHierarchyKey,
      lineItemHierarchyKey,
      shouldCutHierarchy,
    );
  const flightsRows =
    includeFlightLevel && lineItem.flights?.length
      ? lineItem.flights.map((flight, flightIndex) =>
          createFlightRow(
            flight,
            flightIndex,
            campaign,
            campaignIndex,
            lineItemIndex,
            campaignHierarchyKey,
            lineItemHierarchyKey,
            shouldCutHierarchy,
          ),
        )
      : [];

  return [lineItemRow, ...flightsRows].filter(Boolean);
};

const createLineItemRow = (
  lineItem,
  lineItemIndex,
  campaign,
  campaignIndex,
  campaignHierarchyKey,
  lineItemHierarchyKey,
  shouldCutHierarchy,
) => ({
  id: `${campaignIndex}_${lineItemIndex}_li`,
  campaignId: campaign.id,
  lineItemId: lineItem.id,
  type: CAMPAIGN_MANAGEMENT_ROW_TYPE.LINE_ITEM,
  hierarchy: shouldCutHierarchy ? [lineItemHierarchyKey] : [campaignHierarchyKey, lineItemHierarchyKey],
  campaignName: lineItem.name,
  advertiserName: campaign.advertiser.name,
  whitelabel: campaign.whitelabelName,
  status: lineItem.status || campaign.status,
  delivered: lineItem.impressions,
  booked: lineItem.impressions_budget,
  creativesCount: lineItem.creativesCount,
  pacingStatus: lineItem.osi ? getPacingStatus(lineItem.osi) : '',
  pacingTo: lineItem.osi,
  frequencyCaps: lineItem.frequency_caps,
  geo: getGeo(lineItem),
  audience: lineItem.audienceMeta,
  channel: lineItem.channel?.name || '-',
  product: lineItem.product?.name || '—',
  productId: lineItem.product?.id,
  startDate: lineItem.start_date,
  endDate: lineItem.end_date,
  updateDate: lineItem.update_date,
  isArchived: lineItem.is_archived,
  altId: lineItem.alt_id,
  pacing: lineItem.pacing,
  platform: campaign.platform || CampaignPlatformType.Omnichannel,
});

const createFlightRow = (
  flight,
  flightIndex,
  campaign,
  campaignIndex,
  lineItemIndex,
  campaignHierarchyKey,
  lineItemHierarchyKey,
  shouldCutHierarchy,
) => {
  const flightHierarchyKey = nanoid();

  return {
    id: `${campaignIndex}_${lineItemIndex}_${flightIndex}_f`,
    flightId: flight.id,
    type: CAMPAIGN_MANAGEMENT_ROW_TYPE.FLIGHT,
    hierarchy: shouldCutHierarchy
      ? [flightHierarchyKey]
      : [campaignHierarchyKey, lineItemHierarchyKey, flightHierarchyKey],
    campaignName: flight.name,
    advertiserName: campaign.advertiser?.name,
    whitelabel: campaign.whitelabelName,
    status: flight.status,
    booked: flight.impressions_budget,
    startDate: isoDateWithoutTime(flight.start_date),
    endDate: isoDateWithoutTime(flight.end_date),
  };
};

export const getOptionsFromCampaignsList = campaignsList => {
  if (!campaignsList?.length) {
    return [];
  }

  return campaignsList
    .map(campaign => ({
      value: campaign.id,
      label: campaign.name,
    }))
    .sort((a, b) => a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()));
};

const mapFrequencyCaps = frequencyCaps => {
  return removeEmptyKeys({
    daily: Number.parseInt(frequencyCaps.daily),
    weekly: Number.parseInt(frequencyCaps.weekly),
    hourly: Number.parseInt(frequencyCaps.hourly),
    monthly: Number.parseInt(frequencyCaps.monthly),
  });
};

const normalizeCreatives = creatives => {
  return creatives
    .filter(({ disassociated }) => !disassociated)
    .map(({ disassociated, clickUrl, ...rest }) => {
      const normalizedCreative = {
        ...rest,
        endDate: rest.endDate ? moment.utc(rest.endDate).format(DateFormat.Iso8601WithTime) : null,
        startDate: rest.startDate ? moment.utc(rest.startDate).format(DateFormat.Iso8601WithTime) : null,
      };
      const trimmed = typeof clickUrl === 'string' ? clickUrl.trim() : clickUrl;
      return trimmed ? { ...normalizedCreative, clickUrl: normalizeUrl(trimmed) } : normalizedCreative;
    });
};

export const lineItemFormToApiCreate = (
  campaignId,
  {
    impressionsBooked,
    soldCpm,
    bidCpm,
    frequencyCaps,
    startDate,
    endDate,
    duplicates,
    creatives,
    audiences,
    firstPartySegments,
    product,
    channel: channelOption,
    pacingBehavior,
    catchupBehavior,
    dailyBudget,
    dealGroups,
    altId,
    pacing,
    geoFence,
    geo,
    devices,
    hasCustomDomainLists,
    customAllowDomains: _customAllowDomains,
    customBlockDomains: _customBlockDomains,
    packageId,
    ...restData
  },
) => {
  const channelId = channelOption?.value ?? null;
  return {
    campaignId,
    impressionsBudget: Number.parseInt(impressionsBooked),
    soldCpm: Number.parseFloat(soldCpm),
    bidCpm: Number.parseFloat(bidCpm),
    frequencyCaps: mapFrequencyCaps(frequencyCaps),
    duplicates: LineItemService.mapLineItemsDuplicates(duplicates),
    creatives: normalizeCreatives(creatives),
    channelId,
    audiences: AudienceTargetingService.getChannelAudienceTargeting(audiences, channelId),
    firstPartySegments: getControlOptionsValues(firstPartySegments),
    startDate: moment.utc(startDate).format(DateFormat.Iso8601WithTime),
    endDate: moment.utc(endDate).format(DateFormat.Iso8601WithTime),
    productId: hasCustomDomainLists ? undefined : product?.id,
    hasCustomDomainLists: !!hasCustomDomainLists,
    pacingBehavior: pacing === BeeswaxPacing.ASAP ? null : (pacingBehavior ?? BeeswaxPacingBehavior.Even),
    catchupBehavior: pacing === BeeswaxPacing.ASAP ? null : (catchupBehavior ?? BeeswaxCatchupBehavior.Even),
    impressionsBudgetDaily: Number.parseInt(dailyBudget) || null,
    dealGroupIds: getControlOptionsValues(dealGroups),
    altId,
    pacing,
    geoFence: geoFence || null,
    geo: geoFence ? undefined : geo,
    deviceIds: getControlOptionsValues(devices),
    packageId: packageId ? Number(packageId) : null,
    ...restData,
  };
};

export const lineItemFormToApiUpdate = (
  campaignId,
  {
    impressionsBooked,
    soldCpm,
    bidCpm,
    frequencyCaps,
    startDate,
    endDate,
    duplicates,
    creatives,
    audiences,
    firstPartySegments,
    product,
    channel: channelOption,
    dailyBudget,
    dealGroups,
    altId,
    pacing,
    pacingBehavior,
    catchupBehavior,
    geoFence,
    geo,
    devices,
    hasCustomDomainLists,
    customAllowDomains: _customAllowDomainsUpdate,
    customBlockDomains: _customBlockDomainsUpdate,
    packageId,
    ...restData
  },
) => {
  const channelId = channelOption?.value ?? null;
  return {
    campaignId,
    ...(impressionsBooked && { impressionsBudget: Number.parseInt(impressionsBooked) }),
    ...((soldCpm || soldCpm === '') && { soldCpm: Number.parseFloat(soldCpm) || null }),
    ...((bidCpm || bidCpm === '') && { bidCpm: Number.parseFloat(bidCpm) || null }),
    ...((dailyBudget || dailyBudget === '') && { impressionsBudgetDaily: Number.parseInt(dailyBudget) || null }),
    ...(startDate && { startDate: moment.utc(startDate).format(DateFormat.Iso8601WithTime) }),
    ...(endDate && { endDate: moment.utc(endDate).format(DateFormat.Iso8601WithTime) }),
    ...(firstPartySegments && { firstPartySegments: getControlOptionsValues(firstPartySegments) }),
    ...(product && !hasCustomDomainLists && { productId: product.id }),
    ...(hasCustomDomainLists !== undefined && { hasCustomDomainLists: !!hasCustomDomainLists }),
    ...(channelId != null && { channelId }),
    ...(channelId != null &&
      audiences && {
        audiences: AudienceTargetingService.getChannelAudienceTargeting(audiences, channelId),
      }),
    ...(frequencyCaps && {
      frequencyCaps: mapFrequencyCaps(frequencyCaps),
    }),
    ...(duplicates && {
      duplicates: LineItemService.mapLineItemsDuplicates(duplicates),
    }),
    ...(creatives && { creatives: normalizeCreatives(creatives) }),
    ...(dealGroups && { dealGroupIds: getControlOptionsValues(dealGroups) }),
    ...(altId !== undefined && { altId: altId || null }),
    ...(pacing !== undefined && { pacing }),
    ...(pacingBehavior !== undefined && { pacingBehavior }),
    ...(catchupBehavior !== undefined && { catchupBehavior }),
    ...(geoFence !== undefined && { geoFence: geoFence || null }),
    ...(geo !== undefined && { geo: geoFence ? undefined : geo }),
    ...(devices !== undefined && { deviceIds: getControlOptionsValues(devices) }),
    ...(packageId !== undefined && { packageId: packageId ? Number(packageId) : null }),
    ...restData,
  };
};

export const mapLineItemCreativesToFormStructure = creatives =>
  creatives.map(({ startDate, endDate, ...restCreative }) => ({
    ...restCreative,
    startDate: startDate ? moment.utc(startDate) : null,
    endDate: endDate ? moment.utc(endDate) : null,
  }));

export const getAssociatedLineItemCreatives = (creatives, totalWeight) => {
  return creatives.map(creative => {
    const associationWeightRel = creative.associationWeightRel ?? DEFAULT_ASSOCIATION_WEIGHT_REL;
    const associationWeightPct = associationWeightRel / totalWeight;
    return {
      ...creative,
      associationStatus: creative.associationStatus ?? ItemStatus.ACTIVE,
      associationWeightRel,
      associationWeightPct,
      startDate: creative.startDate ? moment.utc(creative.startDate) : null,
      endDate: creative.endDate ? moment.utc(creative.endDate) : null,
    };
  });
};

export const getCampaignsTableAppliedFilters = ({ search, page, limit, sort, ...filters }) => filters;
